import { filterHelpers } from 'global/common/filtering';
import { sortObject } from 'global/common/sorting';
import { gEnums } from 'global/enums/globalEnums';
import { DesktopContext } from 'global/wrappers/DesktopWrapper';
import _ from 'lodash';
import { SportsSeasonContext } from 'projectSpecific/sports/cnr/contexts/SportsSeasonContext';
import { useContext, useMemo } from 'react';
import { FrameworkContext } from '../FrameworkContent';
import { ParentContext } from '../ParentContext';
import { useAppUserAccess } from './useAppUserAccess';

export const usePageItems = () => {

  const _apits = gEnums.additionalPageItemTypes

  const appUserAccess = useAppUserAccess()
  const { accessLevel } = appUserAccess ?? {}

  const parentContexts = useContext(ParentContext)
  const { states, fns } = parentContexts ?? {}
  const {
    paps_state,
    page_state,
    currentSettings_state,
    questionsAndAnswers_state,
  } = states ?? {}

  const { appUser_fns } = fns
  const { otherView, pathViews, view, viewKey } = paps_state ?? {}
  const { aps_global, aps_views } = _.get(currentSettings_state, 'gvviSettings', {});
  const { aps_page } = _.get(page_state, 'pageSettings', {});

  const desktopContext = useContext(DesktopContext);
  const { desktop_state } = desktopContext ?? {}
  const { selectedPage } = desktop_state ?? {}

  const frameworkContext = useContext(FrameworkContext);
  const { framework_state } = frameworkContext ?? {}
  const { desktopMode } = framework_state ?? {}

  const sportsSeasonContext = useContext(SportsSeasonContext)
  const { sportsSeason_state } = sportsSeasonContext ?? {}
  const { matches_info } = sportsSeason_state ?? {}
  const { matches_playoffs } = matches_info ?? {}

  const { viewItems: viewItems_p, layout: layout_page, desktopLayout: desktopLayout_page } = aps_page ?? {}

  const { desktop, appNotifications, appGallery, pageOptions, projectModules } = aps_global ?? {}
  const { useDefaultLayoutType, defaultLayoutType } = desktop ?? {}
  const { showNotificationsOnForeground } = appNotifications ?? {}
  const { allowGallery, galleryPages } = appGallery ?? {}

  const layout = desktopMode && desktopLayout_page ? desktopLayout_page : layout_page
  const { layoutType } = layout ?? {}
  let _layoutType = desktopMode && useDefaultLayoutType && defaultLayoutType ? defaultLayoutType : layoutType

  const { questionsAndAnswers } = questionsAndAnswers_state ?? {}
  const _questionsAndAnswers = questionsAndAnswers && viewKey ? questionsAndAnswers[viewKey] : null

  const { feeds: feeds_asp, geoList: geoList_asp, geoLocation: geoLocation_asp, imageLinks: imageLinks_asp, questionsAndAnswers: questionsAndAnswers_asp, twitter: twitter_asp, videoLinks: videoLinks_asp, voting: voting_asp } = aps_page ?? {}

  const viewItems_page = useMemo(() => {
    if (selectedPage) {
      if (aps_views?.[selectedPage + '_list']) {
        const vip = aps_views?.[selectedPage + '_list'].viewItems
        return vip
      }
    }
    return viewItems_p
  }, [[selectedPage, aps_views]])

  const _shownPageItems = useMemo(() => getShownPageItems(viewItems_page, desktopMode, accessLevel, appUser_fns), [
    pathViews,
    accessLevel,
    desktopMode,
    viewItems_page,
  ]);

  const vips = useMemo(() => {
    let _vips;
    if (otherView && viewItems_page) {
      switch (otherView) {
        case 'add':
        case 'edit':
        case 'media':
          _vips = { [view]: viewItems_page[view] };
          break;
        default:
          break;
      }
    } else {
      switch (_layoutType) {
        default:
          _vips = _shownPageItems;
      }
    }
    return _vips;
  }, [
    _layoutType,
    _shownPageItems,
    viewItems_page,
    otherView,
    pathViews,
    view,
  ]);


  const vips_all = useMemo(() => {
    if (vips) {
      const _vips = addOtherPageItems(_apits, vips, projectModules, pageOptions, aps_page, allowGallery);
      return _vips
    }
    return vips;
  }, [
    _apits,
    _questionsAndAnswers,
    appGallery,
    feeds_asp,
    galleryPages,
    geoList_asp,
    geoLocation_asp,
    imageLinks_asp,
    matches_playoffs,
    pageOptions,
    projectModules,
    questionsAndAnswers_asp,
    twitter_asp,
    videoLinks_asp,
    view,
    viewKey,
    vips,
    voting_asp,
  ],);

  return { vips, vips_all };

}

const getShownPageItems = (viewItems_page, desktopMode, accessLevel, appUser_fns) => {

  // get only viewItems marked with show = true
  const showProp = desktopMode ? gEnums.showTypes.showDesktop : gEnums.showTypes.show

  // list of pageItems with their props filter to shown only

  let pageItems_adjusted = filterHelpers.filterObject(viewItems_page, showProp)

  appUser_fns.validateAccess_items(pageItems_adjusted)

  let splitItems;

  if (pageItems_adjusted) {
    Object.keys(pageItems_adjusted).forEach(key => {
      const pageItemAdjusted = pageItems_adjusted[key]
      const { combine, adminOnly } = pageItemAdjusted ?? {}

      if (adminOnly) {
        if (!accessLevel || accessLevel < gEnums.accessLevels.admin.value) {
          delete pageItems_adjusted[key]
        }
      }

      const { useCombine, combineWith } = combine ?? {}

      // remove combined pageItems
      if (useCombine && combineWith && combineWith.length > 0) {
        pageItemAdjusted.combinedGroups = {}
        combineWith.forEach(key => {
          pageItemAdjusted.combinedGroups[key] = pageItems_adjusted[key]
          delete pageItems_adjusted[key]
        })
      }
    })
    pageItems_adjusted = sortObject(pageItems_adjusted, 'position')
  }

  if (splitItems) {
    Object.keys(splitItems).forEach(k => {
      pageItems_adjusted[k] = splitItems[k]
    })
  }

  return pageItems_adjusted
}

const addOtherPageItems = (_apits, vpias, projectModules, pageOptions, aps_page, allowGallery) => {

  if (!vpias) return {};

  const _dts = gEnums.displayTypes

  const newVpias = { ...vpias };

  const { allowQuestionsAndAnswers, allowVoting } = projectModules ?? {};
  const { allowGeoList, allowGeoLocation, allowFeeds, allowImageLinks, allowTwitter, allowVideoLinks } = pageOptions ?? {};
  const { feeds: feeds_asp, geoList: geoList_asp, geoLocation: geoLocation_asp, imageLinks: imageLinks_asp, questionsAndAnswers: questionsAndAnswers_asp, twitter: twitter_asp, videoLinks: videoLinks_asp, voting: voting_asp } = aps_page ?? {}

  const count = Object.keys(vpias).length;

  // allowQuestionsAndAnswers_g && showOnPage_qAndA && _questionsAndAnsweFrs?.questionActive
  if (allowQuestionsAndAnswers) {
    newVpias[_apits.questionsAndAnswers] = addOtherPageItem(_dts.questionsAndAnswers, questionsAndAnswers_asp, count, 'Q and A');
  }
  if (allowVoting && voting_asp?.allowVoting) {
    newVpias[_apits.voting] = addOtherPageItem(_dts.questionsAndAnswers, questionsAndAnswers_asp, count, 'Q and A');
  }
  if (allowFeeds && feeds_asp?.showFeed) {
    // feedPageItemPosition || count
    newVpias[_apits.feeds] = addOtherPageItem(_dts.feeds, feeds_asp, count, 'Media');
  }
  if (allowGeoList && geoList_asp?.showGeoList) {
    newVpias[_apits.geoList] = addOtherPageItem(_dts.geoList, imageLinks_asp, count, geoList_asp.geoListCaption || null);
  }
  if (allowGeoLocation && geoLocation_asp?.showGeoLocation) {
    newVpias[_apits.geoLocation] = addOtherPageItem(_dts.geoLocation, imageLinks_asp, count);
  }
  if (allowImageLinks && imageLinks_asp?.showImageLinks) {
    newVpias[_apits.imageLinks] = addOtherPageItem(_dts.imageLinks, imageLinks_asp, count);
  }
  if (allowTwitter && twitter_asp?.showTwitter) {
    newVpias[_apits.twitter] = addOtherPageItem(_dts.twitter, twitter_asp, count);
  }
  if (allowVideoLinks && videoLinks_asp?.showVideoLinks) {
    newVpias[_apits.videoLinks] = addOtherPageItem(_dts.videoLinks, videoLinks_asp, count);
  }

  if (allowGallery && galleryPages?.includes(view) && viewKey) {
    newVpias[_apits.photos] = addOtherPageItem(_dts.gallery, appGallery, count);
  }

  return newVpias;
};

const addOtherPageItem = (displayType, componentProps, existingCount, caption) => {
  return {
    key: displayType,
    position: existingCount ? existingCount : 2,
    show: true,
    display: {
      caption: caption ? caption : _.startCase(displayType),
      displayType: displayType,
    },
    _viewPermissionType: gEnums.viewPermissionTypes.allow,
    componentProps
  }
}